/**
 * Marketing Terminal — Analytics Dashboard
 *
 * Master spec §2.6 (Analytics & Attribution). The dedicated growth-marketer
 * dashboard — was previously buried as a tab inside Budget Console.
 *
 * Sections:
 *   1. Hero strip   — 4 top-line KPI tiles with period-over-period deltas
 *   2. Growth trends — time-series chart, followers + engagement per channel
 *   3. Top content  — ranked posts by 2026-weighted engagement score
 *   4. Funnel       — sessions → new users → trials → paid
 *   5. Channel ROI  — per-platform spend, engagement rate, conversions, $/conv
 *
 * All endpoints under /api/admin/marketing/analytics/*, gated by
 * capability marketing.analytics.read.
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Minus,
  Users, MessageSquare, Sparkles, DollarSign,
  Trophy, Activity, BarChart3,
  Eye, Heart, MessageCircle, Bookmark, Share2,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

type Window = 7 | 30 | 90;

const FMT = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString();
const PCT = (n: number | null | undefined, digits = 1) =>
  `${(n ?? 0).toFixed(digits)}%`;

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded-lg bg-white p-4 ${className}`}
    style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}
  >
    {children}
  </div>
);

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  title, icon, children,
}) => (
  <div>
    <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900">
      {icon}
      {title}
    </h2>
    {children}
  </div>
);

const DeltaBadge: React.FC<{ pct: number | null | undefined }> = ({ pct }) => {
  const v = pct ?? 0;
  if (v === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
        <Minus className="h-2.5 w-2.5" /> 0%
      </span>
    );
  }
  const positive = v > 0;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{
        backgroundColor: positive ? 'rgba(31, 108, 79, 0.10)' : 'rgba(220, 38, 38, 0.10)',
        color: positive ? 'var(--cedar-primary)' : 'var(--status-error-strong)',
      }}
    >
      {positive ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
      {positive ? '+' : ''}{v.toFixed(1)}%
    </span>
  );
};

function MarketingAnalyticsContent() {
  const [window, setWindow] = useState<Window>(30);

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Marketing Analytics</h1>
          <p className="text-xs text-gray-500">
            Growth + engagement + conversion across every connected channel. Data lands as platforms connect (see Connections & Keys).
          </p>
        </div>
        <div className="flex gap-1 rounded-md bg-gray-100 p-1">
          {([7, 30, 90] as Window[]).map(w => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`rounded px-3 py-1 text-xs font-medium transition ${
                window === w ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </header>

      <OverviewStrip days={window} />
      <GrowthTrendsSection days={window} />

      <div className="grid gap-6 lg:grid-cols-2">
        <TopContentSection days={window} />
        <FunnelSection days={window} />
      </div>

      <ChannelRoiSection days={window} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 1. Hero strip — 4 top-level KPI tiles
// ─────────────────────────────────────────────────────────────────────────

interface OverviewData {
  followers_total: number;
  followers_delta_pct: number;
  engagement_rate_pct: number;
  engagement_rate_delta_pct: number;
  trials_started: number;
  trials_delta_pct: number;
  conversions_total: number;
  conversions_delta_pct: number;
}

function OverviewStrip({ days }: { days: number }) {
  const { data, isLoading } = useQuery<{ data: OverviewData }>({
    queryKey: ['marketing-analytics-overview', days],
    queryFn: () => adminFetch(`/api/admin/marketing/analytics/overview?days=${days}`),
  });
  const d = data?.data;

  const tiles = [
    {
      label: 'Total followers',
      value: d?.followers_total,
      delta: d?.followers_delta_pct,
      icon: <Users className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />,
      formatter: FMT,
    },
    {
      label: 'Engagement rate',
      value: d?.engagement_rate_pct,
      delta: d?.engagement_rate_delta_pct,
      icon: <MessageSquare className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />,
      formatter: (n: number) => `${n.toFixed(2)}%`,
    },
    {
      label: 'Trials started',
      value: d?.trials_started,
      delta: d?.trials_delta_pct,
      icon: <Sparkles className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />,
      formatter: FMT,
    },
    {
      label: 'Paid conversions',
      value: d?.conversions_total,
      delta: d?.conversions_delta_pct,
      icon: <DollarSign className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />,
      formatter: FMT,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles.map(t => (
        <Card key={t.label}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
              {t.icon}
              {t.label}
            </div>
            <DeltaBadge pct={t.delta} />
          </div>
          <div className="mt-2 text-2xl font-bold text-gray-900">
            {isLoading ? '—' : t.formatter((t.value as number) ?? 0)}
          </div>
          <div className="text-[10px] text-gray-400">vs. prior {days}d</div>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 2. Growth trends — time-series per platform
// ─────────────────────────────────────────────────────────────────────────

interface GrowthRow {
  snapshot_date: string;
  platform: string;
  follows_gained: number;
  engagement: number;
  impressions: number;
}

function GrowthTrendsSection({ days }: { days: number }) {
  const { data, isLoading } = useQuery<{ data: { series: GrowthRow[] } }>({
    queryKey: ['marketing-analytics-growth', days],
    queryFn: () => adminFetch(`/api/admin/marketing/analytics/growth-trends?days=${Math.max(days, 30)}`),
  });

  const series = data?.data?.series ?? [];

  // Pivot for sparkline-style display: per-platform totals over the window
  const byPlatform = new Map<string, { follows: number; engagement: number; days: number }>();
  series.forEach(r => {
    const prev = byPlatform.get(r.platform) ?? { follows: 0, engagement: 0, days: 0 };
    byPlatform.set(r.platform, {
      follows: prev.follows + (r.follows_gained || 0),
      engagement: prev.engagement + (r.engagement || 0),
      days: prev.days + 1,
    });
  });

  return (
    <Section title="Growth & engagement trends" icon={<TrendingUp className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />}>
      <Card>
        {isLoading ? (
          <div className="py-8 text-center text-xs italic text-gray-400">Loading…</div>
        ) : series.length === 0 ? (
          <EmptyState
            title="No growth data yet"
            sub="Once you connect platforms (Connections & Keys) and the daily sync runs, follower deltas + engagement counts land here per day per channel."
          />
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-100 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              <div className="col-span-3">Platform</div>
              <div className="col-span-3 text-right">Follows gained</div>
              <div className="col-span-3 text-right">Engagement</div>
              <div className="col-span-3 text-right">Days reporting</div>
            </div>
            {Array.from(byPlatform.entries()).map(([platform, totals]) => (
              <div key={platform} className="grid grid-cols-12 gap-2 py-1.5 text-xs text-gray-700">
                <div className="col-span-3 font-mono">{platform}</div>
                <div className="col-span-3 text-right font-bold">{FMT(totals.follows)}</div>
                <div className="col-span-3 text-right">{FMT(totals.engagement)}</div>
                <div className="col-span-3 text-right text-gray-400">{totals.days}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3. Top content
// ─────────────────────────────────────────────────────────────────────────

interface TopPost {
  platform: string;
  platform_post_id: string;
  content_text: string | null;
  published_at: string | null;
  posting_account: string | null;
  impressions: number;
  engagement: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  sends: number;
  score: number;
}

function TopContentSection({ days }: { days: number }) {
  const { data, isLoading } = useQuery<{ data: { posts: TopPost[] } }>({
    queryKey: ['marketing-analytics-top-content', days],
    queryFn: () => adminFetch(`/api/admin/marketing/analytics/top-content?days=${days}&limit=10`),
  });
  const posts = data?.data?.posts ?? [];

  return (
    <Section title="Top content this window" icon={<Trophy className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />}>
      <Card>
        {isLoading ? (
          <div className="py-8 text-center text-xs italic text-gray-400">Loading…</div>
        ) : posts.length === 0 ? (
          <EmptyState
            title="No published posts yet"
            sub="Top posts ranked by 2026-weighted engagement score: engagement + 2×saves + 3×sends. Publish content via the Pipeline (and connect platforms) to see this fill in."
          />
        ) : (
          <ol className="space-y-2">
            {posts.map((p, i) => (
              <li key={p.platform_post_id} className="flex items-start gap-3 border-b border-gray-50 pb-2 last:border-0">
                <div className="text-lg font-bold text-gray-400 w-6 shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase text-gray-500">
                    <span className="font-mono">{p.platform}</span>
                    {p.posting_account && <span className="text-gray-400">· @{p.posting_account}</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-700 line-clamp-2">
                    {p.content_text ?? '(post text not loaded)'}
                  </div>
                  <div className="mt-1 flex gap-3 text-[10px] text-gray-500">
                    <span className="inline-flex items-center gap-1"><Eye size={11} />{FMT(p.impressions)}</span>
                    <span className="inline-flex items-center gap-1"><Heart size={11} />{FMT(p.likes)}</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle size={11} />{FMT(p.comments)}</span>
                    <span className="inline-flex items-center gap-1"><Bookmark size={11} />{FMT(p.saves)}</span>
                    <span className="inline-flex items-center gap-1"><Share2 size={11} />{FMT(p.sends + p.shares)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold" style={{ color: 'var(--cedar-primary)' }}>{FMT(p.score)}</div>
                  <div className="text-[9px] uppercase text-gray-400">score</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 4. Funnel
// ─────────────────────────────────────────────────────────────────────────

interface FunnelStep {
  label: string;
  count: number;
  convert_to_next_pct: number | null;
}

function FunnelSection({ days }: { days: number }) {
  const { data, isLoading } = useQuery<{ data: { steps: FunnelStep[] } }>({
    queryKey: ['marketing-analytics-funnel', days],
    queryFn: () => adminFetch(`/api/admin/marketing/analytics/funnel?days=${days}`),
  });
  const steps = data?.data?.steps ?? [];
  const maxCount = Math.max(1, ...steps.map(s => s.count));

  return (
    <Section title="Acquisition funnel" icon={<Activity className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />}>
      <Card>
        {isLoading ? (
          <div className="py-8 text-center text-xs italic text-gray-400">Loading…</div>
        ) : steps.every(s => s.count === 0) ? (
          <EmptyState
            title="No funnel data yet"
            sub="Sessions arrive via GA4 sync; trial + paid events arrive via conversion_events writes from the app's signup + RevenueCat flows."
          />
        ) : (
          <div className="space-y-3">
            {steps.map((s, i) => {
              const widthPct = (s.count / maxCount) * 100;
              return (
                <div key={s.label}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="font-medium text-gray-700">{s.label}</span>
                    <span className="font-bold text-gray-900">{FMT(s.count)}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${widthPct}%`, backgroundColor: 'var(--cedar-primary)' }}
                    />
                  </div>
                  {i < steps.length - 1 && s.convert_to_next_pct !== null && (
                    <div className="mt-1 text-[10px] text-gray-500">
                      ↓ {PCT(s.convert_to_next_pct, 1)} convert to next
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 5. Channel ROI table
// ─────────────────────────────────────────────────────────────────────────

interface ChannelRow {
  platform: string;
  spend_cents: number;
  impressions: number;
  engagement: number;
  clicks: number;
  conversions: number;
  engagement_rate_pct: number;
  cost_per_conversion_cents: number | null;
}

function ChannelRoiSection({ days }: { days: number }) {
  const { data, isLoading } = useQuery<{ data: { rows: ChannelRow[] } }>({
    queryKey: ['marketing-analytics-channel-roi', days],
    queryFn: () => adminFetch(`/api/admin/marketing/analytics/channel-roi?days=${days}`),
  });
  const rows = data?.data?.rows ?? [];

  return (
    <Section title="Channel ROI" icon={<BarChart3 className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />}>
      <Card>
        {isLoading ? (
          <div className="py-8 text-center text-xs italic text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No channel data yet"
            sub="Once posts publish + ads run + the daily sync cron lands data, every channel's spend, impressions, engagement rate, and cost-per-conversion will surface here."
          />
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="pb-2 text-left">Platform</th>
                <th className="pb-2 text-right">Spend</th>
                <th className="pb-2 text-right">Impressions</th>
                <th className="pb-2 text-right">Engagement</th>
                <th className="pb-2 text-right">Eng rate</th>
                <th className="pb-2 text-right">Clicks</th>
                <th className="pb-2 text-right">Conversions</th>
                <th className="pb-2 text-right">$/conv</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.platform} className="border-b border-gray-50">
                  <td className="py-2 font-mono">{r.platform}</td>
                  <td className="text-right">${(r.spend_cents / 100).toFixed(2)}</td>
                  <td className="text-right">{FMT(r.impressions)}</td>
                  <td className="text-right">{FMT(r.engagement)}</td>
                  <td className="text-right font-semibold" style={{ color: r.engagement_rate_pct >= 3 ? 'var(--cedar-primary)' : 'var(--text-secondary)' }}>
                    {PCT(r.engagement_rate_pct, 2)}
                  </td>
                  <td className="text-right">{FMT(r.clicks)}</td>
                  <td className="text-right font-bold">{FMT(r.conversions)}</td>
                  <td className="text-right">
                    {r.cost_per_conversion_cents !== null ? `$${(r.cost_per_conversion_cents / 100).toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ title: string; sub: string }> = ({ title, sub }) => (
  <div className="py-10 text-center">
    <BarChart3 className="mx-auto mb-2 h-8 w-8 text-gray-300" />
    <div className="text-xs font-semibold text-gray-500">{title}</div>
    <div className="mx-auto mt-1 max-w-md text-[11px] text-gray-400">{sub}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Default export wrapped in QueryClientProvider (mirrors other marketing pages)
// ─────────────────────────────────────────────────────────────────────────

export const MarketingAnalyticsPage: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <MarketingAnalyticsContent />
  </QueryClientProvider>
);

export default MarketingAnalyticsPage;
